import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import AppLayout from '../../components/AppLayout';
import { S, Icon, Icons, StatusBadge, BtnPrimary, TH, TD, formatDate } from '../regureStyles';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

// ─── Metric Card ─────────────────────────────────────────
const cardStyle = {
  flex: 1, background: '#fff', borderRadius: 12, padding: '24px 20px',
  border: `1px solid ${S.borderLight}`, display: 'flex', alignItems: 'center', gap: 16,
};
const iconWrap = (bg) => ({
  width: 48, height: 48, borderRadius: 10, background: bg,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
});

const MetricCard = ({ label, value, sub, icon, color }) => (
  <div style={cardStyle}>
    <div style={iconWrap(color + '14')}>
      <Icon name={icon} color={color} size={22} />
    </div>
    <div>
      <div style={{ fontSize: 12, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color: S.textPrimary, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: S.textMuted, marginTop: 4 }}>{sub}</div>}
    </div>
  </div>
);

// ─── Status Badge for Org ────────────────────────────────
const OrgStatusBadge = ({ active }) => {
  const { t } = useTranslation();
  const bg = active ? '#dcfce7' : '#fee2e2';
  const color = active ? '#15803d' : '#991b1b';
  const label = active ? t('screens.superAdmin.orgStatusActive') : t('screens.superAdmin.orgStatusInactive');
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 12px', borderRadius: 20,
      fontSize: 12, fontWeight: 600, background: bg, color,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />
      {label}
    </span>
  );
};

// ─── SuperAdminDashboard ─────────────────────────────────
const SuperAdminDashboard = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [stats, setStats] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('orgs'); // 'orgs' | 'billing' | 'packages'
  const [billing, setBilling] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [reggieModal, setReggieModal] = useState(null); // { orgId, orgName, aiEnabled, aiEnabledUntil }
  const [reggieUpdating, setReggieUpdating] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsResp, orgsResp] = await Promise.all([
        axios.get(`${BASE_URL}/orgs/platform-stats`, { withCredentials: true }).catch(() => null),
        axios.get(`${BASE_URL}/orgs/`, { withCredentials: true }).catch(() => null),
      ]);
      if (statsResp?.data?.data) setStats(statsResp.data.data);
      if (Array.isArray(orgsResp?.data?.data?.data)) setOrgs(orgsResp.data.data.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBilling = useCallback(async () => {
    setBillingLoading(true);
    try {
      const resp = await axios.get(`${BASE_URL}/orgs/billing-overview`, { withCredentials: true });
      if (resp?.data?.data) setBilling(resp.data.data);
    } catch { /* silent */ } finally { setBillingLoading(false); }
  }, []);

  const fetchPackages = useCallback(async () => {
    setPackagesLoading(true);
    try {
      const resp = await axios.get(`${BASE_URL}/packages/get-packages`, { withCredentials: true });
      if (Array.isArray(resp?.data?.data)) setPackages(resp.data.data);
    } catch { /* silent */ } finally { setPackagesLoading(false); }
  }, []);

  useEffect(() => {
    if (activeTab === 'billing' && !billing) fetchBilling();
    if (activeTab === 'packages' && packages.length === 0) fetchPackages();
  }, [activeTab, billing, packages, fetchBilling, fetchPackages]);

  useEffect(() => { fetchData(); }, [fetchData]);
  // billing fetch is deferred until tab is activated (above)

  const totalOrgs = stats?.totalOrgs ?? orgs.length;
  const activeOrgs = stats?.activeOrgs ?? orgs.filter(o => o.active !== false).length;
  const totalUsers = stats?.totalUsers ?? 0;
  const openClaims = stats?.openClaims ?? 0;
  const premiumVolume = stats?.premiumVolume ?? 0;

  const filteredOrgs = orgs.filter(o => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (o.name || '').toLowerCase().includes(q) || (o.email || '').toLowerCase().includes(q);
  });

  const formatStorage = (bytes) => {
    if (!bytes) return '0 MB';
    if (bytes >= 1073741824) return (bytes / 1073741824).toFixed(1) + ' GB';
    return (bytes / 1048576).toFixed(0) + ' MB';
  };

  const formatNumber = (num) => {
    if (!num && num !== 0) return '—';
    return String(num).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const updateReggieSettings = useCallback(async (orgId, aiEnabled, enableTrial) => {
    setReggieUpdating(true);
    try {
      const resp = await axios.patch(`${BASE_URL}/orgs/${orgId}/reggie-settings`, {
        aiEnabled,
        enableTrial,
      }, { withCredentials: true });

      if (resp.data?.data?.org) {
        // Update local orgs list with new Reggie settings
        setOrgs(prev => prev.map(o =>
          o._id === orgId
            ? { ...o, aiEnabled: resp.data.data.org.aiEnabled, aiEnabledUntil: resp.data.data.org.aiEnabledUntil }
            : o
        ));
        setReggieModal(null);
      }
    } catch (err) {
      console.error('Failed to update Reggie settings:', err.response?.data?.message || err.message);
    } finally {
      setReggieUpdating(false);
    }
  }, []);

  const openReggieModal = useCallback((org) => {
    setReggieModal({
      orgId: org._id,
      orgName: org.name,
      aiEnabled: org.aiEnabled || false,
      aiEnabledUntil: org.aiEnabledUntil,
    });
  }, []);

  if (loading) {
    return (
      <AppLayout>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 60 }}>
          <div style={{ width: 36, height: 36, border: '3px solid #e2e8f0', borderTopColor: S.burgundy, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto' }}>

        {/* Header */}
        <header style={{
          padding: '20px 32px', borderBottom: `1px solid ${S.borderLight}`,
          background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(20px)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: 12, color: S.textMuted, marginBottom: 4 }}>{t('screens.superAdmin.eyebrow')}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: S.textPrimary }}>{t('screens.superAdmin.title')}</div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button onClick={fetchData} style={{ padding: '9px 16px', background: S.bgCard, border: `1px solid ${S.borderLight}`, borderRadius: 10, fontSize: 13, fontWeight: 500, color: S.textSecondary, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="refresh" color={S.textMuted} size={14} /> {t('screens.superAdmin.refresh')}
            </button>
            <BtnPrimary onClick={() => history.push('/orgs/onboard')}>
              <Icon name="plus" color="#fff" size={16} />
              {t('screens.superAdmin.onboardNewOrg')}
            </BtnPrimary>
          </div>
        </header>

        <div style={{ flex: 1, padding: '24px 32px', display: 'flex', flexDirection: 'column', gap: 24, minHeight: 0 }}>

          {/* Metric Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
            <MetricCard
              label={t('screens.superAdmin.metricTotalOrgs')}
              value={`${activeOrgs} / ${totalOrgs}`}
              sub={t('screens.superAdmin.metricActiveSub', { count: activeOrgs })}
              icon="building"
              color={S.burgundy}
            />
            <MetricCard
              label={t('screens.superAdmin.metricTotalUsers')}
              value={String(totalUsers)}
              sub={t('screens.superAdmin.metricTotalUsersSub')}
              icon="users"
              color={S.info}
            />
            <MetricCard
              label={t('screens.superAdmin.metricActiveClaims')}
              value={String(openClaims)}
              sub={t('screens.superAdmin.metricActiveClaimsSub')}
              icon="file-text"
              color={S.warning}
            />
            <MetricCard
              label={t('screens.superAdmin.metricPremiumVolume')}
              value={premiumVolume > 0 ? `$${Number(premiumVolume).toLocaleString()}` : '$0'}
              sub={t('screens.superAdmin.metricPremiumVolumeSub')}
              icon="dollar-sign"
              color={S.success}
            />
          </div>

          {/* Tab Bar */}
          <div style={{ display: 'flex', gap: 4, background: S.bgCard, borderRadius: 10, padding: 4, alignSelf: 'flex-start' }}>
            {[{ id: 'orgs', label: t('screens.superAdmin.tabOrgs') }, { id: 'billing', label: t('screens.superAdmin.tabBilling') }, { id: 'packages', label: t('screens.superAdmin.tabPackages') }].map(opt => (
              <button key={opt.id} onClick={() => setActiveTab(opt.id)} style={{ padding: '7px 18px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: activeTab === opt.id ? '#fff' : 'transparent', color: activeTab === opt.id ? S.burgundy : S.textMuted, boxShadow: activeTab === opt.id ? '0 1px 4px rgba(0,0,0,0.08)' : 'none', transition: 'all 0.15s' }}>
                {opt.label}
              </button>
            ))}
          </div>

          {/* ── Billing Tab ────────────────── */}
          {activeTab === 'billing' && (
            <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 16, overflow: 'hidden', flex: 1 }}>
              <div style={{ padding: '20px 24px', borderBottom: `1px solid ${S.borderLight}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: S.textPrimary }}>{t('screens.superAdmin.billingSectionTitle')}</div>
                  <div style={{ fontSize: 12, color: S.textMuted, marginTop: 2 }}>
                    {billing ? t('screens.superAdmin.billingSummary', { mrr: Number(billing.totalMrr).toLocaleString('en-US', { minimumFractionDigits: 0 }), subscribed: billing.subscribed }) : t('screens.superAdmin.billingLoadingText')}
                  </div>
                </div>
                <button onClick={fetchBilling} style={{ padding: '8px 14px', background: S.bgCard, border: `1px solid ${S.borderLight}`, borderRadius: 8, fontSize: 13, fontWeight: 500, color: S.textSecondary, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Icon name="refresh" color={S.textMuted} size={14} /> {t('screens.superAdmin.refresh')}
                </button>
              </div>
              {billingLoading ? (
                <div style={{ padding: 40, textAlign: 'center', color: S.textMuted }}>{t('screens.superAdmin.loading')}</div>
              ) : billing?.orgs ? (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <TH>{t('screens.superAdmin.colOrganization')}</TH>
                        <TH>{t('screens.superAdmin.colStatus')}</TH>
                        <TH>{t('screens.superAdmin.colPlan')}</TH>
                        <TH>{t('screens.superAdmin.colSeats')}</TH>
                        <TH>{t('screens.superAdmin.colMrr')}</TH>
                        <TH>{t('screens.superAdmin.colTrialEnds')}</TH>
                        <TH style={{ textAlign: 'right' }}>{t('screens.superAdmin.colActions')}</TH>
                      </tr>
                    </thead>
                    <tbody>
                      {billing.orgs.map((org, i) => (
                        <tr key={org._id} style={{ borderBottom: `1px solid ${S.borderLight}`, background: i % 2 === 0 ? '#fff' : S.bgCard }}>
                          <TD>
                            <div style={{ fontSize: 13, fontWeight: 600, color: S.textPrimary }}>{org.name}</div>
                            <div style={{ fontSize: 11, color: S.textMuted }}>{org.email}</div>
                          </TD>
                          <TD>
                            <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: org.subscriptionStatus === 'active' ? '#dcfce7' : org.subscriptionStatus === 'trial' ? '#fef9c3' : '#fee2e2', color: org.subscriptionStatus === 'active' ? '#15803d' : org.subscriptionStatus === 'trial' ? '#854d0e' : '#991b1b' }}>
                              {org.subscriptionStatus}
                            </span>
                          </TD>
                          <TD style={{ color: S.textSecondary }}>{org.plan || '—'}</TD>
                          <TD style={{ fontWeight: 600, color: S.textPrimary }}>{org.seats}</TD>
                          <TD style={{ fontWeight: 700, color: org.mrr > 0 ? S.success : S.textMuted }}>{org.mrr > 0 ? `$${org.mrr.toLocaleString()}` : '—'}</TD>
                          <TD style={{ color: S.textMuted, fontSize: 12 }}>{org.trialEndsAt ? formatDate(org.trialEndsAt) : '—'}</TD>
                          <TD style={{ textAlign: 'right' }}>
                            <button onClick={() => history.push(`/orgs/${org._id}`)} style={{ padding: '5px 12px', borderRadius: 7, background: `rgba(128,0,32,0.06)`, border: `1px solid rgba(128,0,32,0.15)`, fontSize: 12, fontWeight: 600, color: S.burgundy, cursor: 'pointer' }}>
                              {t('screens.superAdmin.view')}
                            </button>
                          </TD>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ padding: 40, textAlign: 'center', color: S.textMuted }}>{t('screens.superAdmin.noBilling')}</div>
              )}
            </div>
          )}

          {/* ── Pricing Packages ──────────── */}
          {activeTab === 'packages' && (
            <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 16, overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '20px 24px', borderBottom: `1px solid ${S.borderLight}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: S.textPrimary }}>{t('screens.superAdmin.pricingSectionTitle')}</div>
                  <div style={{ fontSize: 12, color: S.textMuted, marginTop: 2 }}>
                    {packages.length > 0 ? t('screens.superAdmin.plansAvailable', { count: packages.length }) : t('screens.superAdmin.plansLoading')}
                  </div>
                </div>
                <button onClick={fetchPackages} style={{ padding: '8px 14px', background: S.bgCard, border: `1px solid ${S.borderLight}`, borderRadius: 8, fontSize: 13, fontWeight: 500, color: S.textSecondary, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Icon name="refresh" color={S.textMuted} size={14} /> {t('screens.superAdmin.refresh')}
                </button>
              </div>
              {packagesLoading ? (
                <div style={{ padding: 40, textAlign: 'center', color: S.textMuted }}>{t('screens.superAdmin.loading')}</div>
              ) : packages.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: S.textMuted }}>{t('screens.superAdmin.noPackages')}</div>
              ) : (
                <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
                  {/* Pricing Grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 24, marginBottom: 40 }}>
                    {packages.map(pkg => {
                      const monthlyPrice = pkg.prices?.find(p => p.recurring?.interval === 'month' && !p.nickname?.includes('Annual'));
                      const annualPrice = pkg.prices?.find(p => p.recurring?.interval === 'month' && p.nickname?.includes('Annual'));
                      const monthlyAmount = monthlyPrice ? (monthlyPrice.unit_amount / 100).toFixed(0) : 'N/A';
                      const annualAmount = annualPrice ? (annualPrice.unit_amount / 100).toFixed(0) : 'N/A';
                      const claimsQuota = pkg.metadata?.claimsPerMonth || 0;

                      // Pricing rationale
                      const rationale = {
                        Starter: t('screens.superAdmin.starterRationale'),
                        Professional: t('screens.superAdmin.professionalRationale'),
                        Enterprise: t('screens.superAdmin.enterpriseRationale'),
                      };

                      // Overage rates
                      const overageRate = pkg.name === 'Starter' ? '$1.50' : pkg.name === 'Professional' ? '$1.00' : '$0.50';

                      return (
                        <div key={pkg.id} style={{
                          background: '#fff', border: `2px solid ${S.borderLight}`, borderRadius: 16, overflow: 'hidden',
                        }}>
                          {/* Header with Tier Name */}
                          <div style={{ background: S.bgCard, padding: '20px 24px', borderBottom: `1px solid ${S.borderLight}` }}>
                            <div style={{ fontSize: 20, fontWeight: 800, color: S.textPrimary, marginBottom: 4 }}>{pkg.name}</div>
                            <div style={{ fontSize: 12, color: S.textMuted, lineHeight: 1.5 }}>{rationale[pkg.name]}</div>
                          </div>

                          {/* Pricing */}
                          <div style={{ padding: '24px' }}>
                            <div style={{ marginBottom: 20 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: S.textSecondary, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 12 }}>{t('screens.superAdmin.perSeatPricing')}</div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                <div>
                                  <div style={{ fontSize: 12, color: S.textMuted, marginBottom: 6 }}>{t('screens.superAdmin.monthly')}</div>
                                  <div style={{ fontSize: 28, fontWeight: 800, color: S.burgundy }}>${monthlyAmount}</div>
                                  <div style={{ fontSize: 11, color: S.textMuted, marginTop: 2 }}>{t('screens.superAdmin.perUserMonth')}</div>
                                </div>
                                <div>
                                  <div style={{ fontSize: 12, color: S.textMuted, marginBottom: 6 }}>{t('screens.superAdmin.annual')}</div>
                                  <div style={{ fontSize: 28, fontWeight: 800, color: S.burgundy }}>${annualAmount}</div>
                                  <div style={{ fontSize: 11, color: S.textMuted, marginTop: 2 }}>{t('screens.superAdmin.perUserMonthDiscount')}</div>
                                </div>
                              </div>
                            </div>

                            {/* Claims Quota */}
                            <div style={{ borderTop: `1px solid ${S.borderLight}`, paddingTop: 20, marginBottom: 20 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: S.textSecondary, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 12 }}>{t('screens.superAdmin.claimsProcessing')}</div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                <div>
                                  <div style={{ fontSize: 12, color: S.textMuted, marginBottom: 6 }}>{t('screens.superAdmin.baseQuota')}</div>
                                  <div style={{ fontSize: 18, fontWeight: 700, color: S.textPrimary }}>{formatNumber(claimsQuota)}</div>
                                  <div style={{ fontSize: 11, color: S.textMuted, marginTop: 2 }}>{t('screens.superAdmin.claimsMonthIncluded')}</div>
                                </div>
                                <div>
                                  <div style={{ fontSize: 12, color: S.textMuted, marginBottom: 6 }}>{t('screens.superAdmin.overage')}</div>
                                  <div style={{ fontSize: 18, fontWeight: 700, color: S.burgundy }}>{overageRate}</div>
                                  <div style={{ fontSize: 11, color: S.textMuted, marginTop: 2 }}>{t('screens.superAdmin.per100Claims')}</div>
                                </div>
                              </div>
                              <div style={{ fontSize: 11, color: S.textMuted, marginTop: 10, fontStyle: 'italic', lineHeight: 1.5 }}>
                                {t('screens.superAdmin.capacityNote')}
                              </div>
                            </div>

                            {/* Limits */}
                            <div style={{ borderTop: `1px solid ${S.borderLight}`, paddingTop: 20, marginBottom: 20 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: S.textSecondary, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 12 }}>{t('screens.superAdmin.infraLimits')}</div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, fontSize: 13 }}>
                                <div>
                                  <span style={{ color: S.textMuted }}>{t('screens.superAdmin.maxUsers')}</span>
                                  <div style={{ fontWeight: 600, color: S.textPrimary, marginTop: 4 }}>{formatNumber(pkg.metadata?.users)}</div>
                                </div>
                                <div>
                                  <span style={{ color: S.textMuted }}>{t('screens.superAdmin.storage')}</span>
                                  <div style={{ fontWeight: 600, color: S.textPrimary, marginTop: 4 }}>{formatNumber(pkg.metadata?.space)} GB</div>
                                </div>
                              </div>
                            </div>

                            {/* Why Higher Price */}
                            <div style={{ borderTop: `1px solid ${S.borderLight}`, paddingTop: 20, background: S.bgCard, marginLeft: '-24px', marginRight: '-24px', marginBottom: '-24px', padding: '20px 24px' }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: S.burgundy, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>{t('screens.superAdmin.whyPrice')}</div>
                              <div style={{ fontSize: 12, color: S.textMuted, lineHeight: 1.6 }}>
                                {pkg.name === 'Starter' && t('screens.superAdmin.starterDesc')}
                                {pkg.name === 'Professional' && t('screens.superAdmin.professionalDesc')}
                                {pkg.name === 'Enterprise' && t('screens.superAdmin.enterpriseDesc')}
                              </div>
                            </div>

                            {pkg.active === false && (
                              <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', padding: '8px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, textAlign: 'center', marginTop: 16 }}>
                                {t('screens.superAdmin.inactiveWarn')}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Add-Ons Section */}
                  <div style={{ borderTop: `2px solid ${S.borderLight}`, paddingTop: 32, marginTop: 32 }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: S.textPrimary, marginBottom: 6 }}>{t('screens.superAdmin.addOnsTitle')}</div>
                    <div style={{ fontSize: 12, color: S.textMuted, marginBottom: 24 }}>{t('screens.superAdmin.addOnsBlurb')}</div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
                      <div style={{ background: S.bgCard, border: `1px solid ${S.borderLight}`, borderRadius: 12, padding: 18 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: S.textPrimary, marginBottom: 4 }}>{t('screens.superAdmin.addOnIntegrationsTitle')}</div>
                        <div style={{ fontSize: 12, color: S.textMuted, marginBottom: 12 }}>{t('screens.superAdmin.addOnIntegrationsSub')}</div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: S.burgundy, marginBottom: 8 }}>{t('screens.superAdmin.addOnIntegrationsPrice')}<span style={{ fontSize: 12, color: S.textMuted, fontWeight: 400 }}>{t('screens.superAdmin.perMonthSuffix')}</span></div>
                        <div style={{ fontSize: 11, color: S.textMuted }}>{t('screens.superAdmin.addOnIntegrationsNote')}</div>
                      </div>

                      <div style={{ background: S.bgCard, border: `1px solid ${S.borderLight}`, borderRadius: 12, padding: 18 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: S.textPrimary, marginBottom: 4 }}>{t('screens.superAdmin.addOnWorkflowsTitle')}</div>
                        <div style={{ fontSize: 12, color: S.textMuted, marginBottom: 12 }}>{t('screens.superAdmin.addOnWorkflowsSub')}</div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: S.burgundy, marginBottom: 8 }}>{t('screens.superAdmin.addOnWorkflowsPrice')}<span style={{ fontSize: 12, color: S.textMuted, fontWeight: 400 }}>{t('screens.superAdmin.perMonthSuffix')}</span></div>
                        <div style={{ fontSize: 11, color: S.textMuted }}>{t('screens.superAdmin.addOnWorkflowsNote')}</div>
                      </div>

                      <div style={{ background: S.bgCard, border: `1px solid ${S.borderLight}`, borderRadius: 12, padding: 18 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: S.textPrimary, marginBottom: 4 }}>{t('screens.superAdmin.addOnApiTitle')}</div>
                        <div style={{ fontSize: 12, color: S.textMuted, marginBottom: 12 }}>{t('screens.superAdmin.addOnApiSub')}</div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: S.burgundy, marginBottom: 8 }}>{t('screens.superAdmin.addOnApiPrice')}<span style={{ fontSize: 12, color: S.textMuted, fontWeight: 400 }}>{t('screens.superAdmin.perMonthSuffix')}</span></div>
                        <div style={{ fontSize: 11, color: S.textMuted }}>{t('screens.superAdmin.addOnApiNote')}</div>
                      </div>

                      <div style={{ background: S.bgCard, border: `1px solid ${S.borderLight}`, borderRadius: 12, padding: 18 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: S.textPrimary, marginBottom: 4 }}>{t('screens.superAdmin.addOnReportsTitle')}</div>
                        <div style={{ fontSize: 12, color: S.textMuted, marginBottom: 12 }}>{t('screens.superAdmin.addOnReportsSub')}</div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: S.burgundy, marginBottom: 8 }}>{t('screens.superAdmin.addOnReportsPrice')}<span style={{ fontSize: 12, color: S.textMuted, fontWeight: 400 }}>{t('screens.superAdmin.perMonthSuffix')}</span></div>
                        <div style={{ fontSize: 11, color: S.textMuted }}>{t('screens.superAdmin.addOnReportsNote')}</div>
                      </div>
                    </div>

                    <div style={{ background: '#f0f9ff', border: `2px solid ${S.burgundy}`, borderRadius: 12, padding: 20, marginTop: 32 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: S.burgundy, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                        {t('screens.superAdmin.profServicesTitle')}
                      </div>
                      <div style={{ fontSize: 12, color: S.textSecondary, lineHeight: 1.7 }}>
                        {t('screens.superAdmin.profServicesBody')}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Org Health Table ───────────── */}
          {activeTab === 'orgs' && (
            <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 16, overflow: 'hidden', flex: 1 }}>
            {/* Table Header */}
            <div style={{ padding: '20px 24px', borderBottom: `1px solid ${S.borderLight}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: S.textPrimary }}>{t('screens.superAdmin.orgHealthTitle')}</div>
                <div style={{ fontSize: 12, color: S.textMuted, marginTop: 2 }}>{orgs.length !== 1 ? t('screens.superAdmin.orgsRegisteredPlural', { count: orgs.length }) : t('screens.superAdmin.orgsRegistered', { count: orgs.length })}</div>
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <div style={{ position: 'relative' }}>
                  <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }}>
                    <Icon name="search" color="#94a3b8" size={15} />
                  </div>
                  <input
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder={t('screens.superAdmin.searchOrgsPh')}
                    style={{ width: 280, padding: '9px 12px 9px 36px', background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 8, fontSize: 13, color: S.textPrimary, outline: 'none', fontFamily: 'inherit' }}
                  />
                </div>
              </div>
            </div>

            {/* Table */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <TH>{t('screens.superAdmin.colOrgName')}</TH>
                    <TH>{t('screens.superAdmin.colStatus')}</TH>
                    <TH>{t('screens.superAdmin.colUsers')}</TH>
                    <TH>{t('screens.superAdmin.colActiveClaims')}</TH>
                    <TH>{t('screens.superAdmin.colStorageUsed')}</TH>
                    <TH>{t('screens.superAdmin.colReggieAi')}</TH>
                    <TH>{t('screens.superAdmin.colLastActivity')}</TH>
                    <TH style={{ textAlign: 'right' }}>{t('screens.superAdmin.colActions')}</TH>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrgs.length === 0 && (
                    <tr>
                      <TD style={{ textAlign: 'center', padding: '40px 14px', color: S.textMuted }} colSpan="7">
                        {orgs.length === 0 ? t('screens.superAdmin.noOrgsFound') : t('screens.superAdmin.noSearchResults')}
                      </TD>
                    </tr>
                  )}
                  {filteredOrgs.map((org, i) => (
                    <tr
                      key={org._id}
                      style={{ borderBottom: `1px solid ${S.borderLight}`, cursor: 'pointer', background: i % 2 === 0 ? '#fff' : S.bgCard, transition: 'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = S.bgHover}
                      onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : S.bgCard}
                    >
                      <TD>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{
                            width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                            background: `linear-gradient(135deg, ${S.burgundy}, ${S.burgundy2})`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#fff', fontSize: 12, fontWeight: 700,
                          }}>
                            {(org.name || 'O').slice(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: S.textPrimary }}>{org.name || t('screens.superAdmin.unnamed')}</div>
                            <div style={{ fontSize: 11, color: S.textMuted }}>{org.email || ''}</div>
                          </div>
                        </div>
                      </TD>
                      <TD><OrgStatusBadge active={org.active !== false} /></TD>
                      <TD style={{ fontWeight: 600, color: S.textPrimary }}>{org.userCount ?? org.users?.length ?? 0}</TD>
                      <TD style={{ fontWeight: 600, color: S.textPrimary }}>{org.openClaimsCount ?? 0}</TD>
                      <TD>{formatStorage(org.storageUsed || 0)}</TD>
                      <TD>
                        {org.aiEnabled ? (
                          <span style={{
                            padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                            background: '#dcfce7', color: '#15803d',
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                          }}>
                            {t('screens.superAdmin.aiEnabled')} {org.aiEnabledUntil && t('screens.superAdmin.aiExpires', { date: formatDate(org.aiEnabledUntil) })}
                          </span>
                        ) : (
                          <span style={{ fontSize: 12, color: S.textMuted }}>{t('screens.superAdmin.aiDisabled')}</span>
                        )}
                      </TD>
                      <TD>{org.lastActivityAt ? formatDate(org.lastActivityAt) : '—'}</TD>
                      <TD style={{ textAlign: 'right', display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); openReggieModal(org); }}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '6px 12px', borderRadius: 8,
                            background: 'rgba(107,114,128,0.06)', border: `1px solid rgba(107,114,128,0.15)`,
                            fontSize: 12, fontWeight: 600, color: '#4b5563', cursor: 'pointer',
                          }}
                        >
                          <Icon name="settings" color="#4b5563" size={14} /> {t('screens.superAdmin.reggie')}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); history.push(`/orgs/${org._id}`); }}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '6px 14px', borderRadius: 8,
                            background: 'rgba(128,0,32,0.06)', border: `1px solid rgba(128,0,32,0.15)`,
                            fontSize: 12, fontWeight: 600, color: S.burgundy, cursor: 'pointer',
                          }}
                        >
                          <Icon name="eye" color={S.burgundy} size={14} /> {t('screens.superAdmin.view')}
                        </button>
                      </TD>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Reggie AI Settings Modal ──────────────────── */}
      {reggieModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, backdropFilter: 'blur(4px)',
        }} onClick={() => setReggieModal(null)}>
          <div
            style={{
              background: '#fff', borderRadius: 16, padding: '32px', maxWidth: 420,
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)', border: `1px solid ${S.borderLight}`,
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: S.textPrimary }}>{t('screens.superAdmin.reggieModalTitle')}</div>
              <div style={{ fontSize: 13, color: S.textMuted, marginTop: 4 }}>{reggieModal.orgName}</div>
            </div>

            {/* Status */}
            <div style={{ marginBottom: 20, padding: '12px 14px', background: S.bgCard, borderRadius: 8, borderLeft: `4px solid ${reggieModal.aiEnabled ? '#15803d' : '#94a3b8'}` }}>
              <div style={{ fontSize: 12, color: S.textMuted, marginBottom: 4 }}>{t('screens.superAdmin.currentStatus')}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: S.textPrimary }}>
                {reggieModal.aiEnabled ? (
                  <>
                    {t('screens.superAdmin.aiEnabled')} {reggieModal.aiEnabledUntil && t('screens.superAdmin.aiExpires', { date: formatDate(reggieModal.aiEnabledUntil) })}
                  </>
                ) : (
                  t('screens.superAdmin.disabledLong')
                )}
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
              {reggieModal.aiEnabled ? (
                <>
                  <button
                    disabled={reggieUpdating}
                    onClick={() => updateReggieSettings(reggieModal.orgId, false, false)}
                    style={{
                      padding: '10px 16px', borderRadius: 8, border: '1px solid #fee2e2',
                      background: '#fef2f2', color: '#991b1b', fontSize: 13, fontWeight: 600,
                      cursor: reggieUpdating ? 'not-allowed' : 'pointer', opacity: reggieUpdating ? 0.6 : 1,
                    }}
                  >
                    {reggieUpdating ? t('screens.superAdmin.updating') : t('screens.superAdmin.disableReggie')}
                  </button>
                </>
              ) : (
                <button
                  disabled={reggieUpdating}
                  onClick={() => updateReggieSettings(reggieModal.orgId, true, false)}
                  style={{
                    padding: '10px 16px', borderRadius: 8, border: `1px solid ${S.borderLight}`,
                    background: '#fff', color: S.textPrimary, fontSize: 13, fontWeight: 600,
                    cursor: reggieUpdating ? 'not-allowed' : 'pointer', opacity: reggieUpdating ? 0.6 : 1,
                  }}
                >
                  {reggieUpdating ? t('screens.superAdmin.updating') : t('screens.superAdmin.enableReggie')}
                </button>
              )}

              <button
                disabled={reggieUpdating}
                onClick={() => updateReggieSettings(reggieModal.orgId, true, true)}
                style={{
                  padding: '10px 16px', borderRadius: 8, border: 'none',
                  background: S.burgundy, color: '#fff', fontSize: 13, fontWeight: 600,
                  cursor: reggieUpdating ? 'not-allowed' : 'pointer', opacity: reggieUpdating ? 0.6 : 1,
                }}
              >
                {reggieUpdating ? t('screens.superAdmin.updating') : t('screens.superAdmin.startTrial')}
              </button>
            </div>

            {/* Close Button */}
            <button
              onClick={() => setReggieModal(null)}
              disabled={reggieUpdating}
              style={{
                width: '100%', padding: '10px 16px', borderRadius: 8, border: `1px solid ${S.borderLight}`,
                background: S.bgCard, color: S.textSecondary, fontSize: 13, fontWeight: 600,
                cursor: reggieUpdating ? 'not-allowed' : 'pointer', opacity: reggieUpdating ? 0.6 : 1,
              }}
            >
              {t('screens.superAdmin.close')}
            </button>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default SuperAdminDashboard;
